import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { auditApi } from '../api/audit';
import { toErrorMessage } from '../api/client';
import { useStores } from '../auth/StoreContext';
import { Card, PageHead } from '../components/ui';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { StoreSelector } from '../components/StoreSelector';
import { dateTime } from '../lib/format';

const PAGE_SIZE = 25;

export function AuditLogsPage() {
  const { selectedStoreId } = useStores();
  const [page, setPage] = useState(0);

  const q = useQuery({
    queryKey: ['audit', selectedStoreId, page],
    queryFn: () => auditApi.list({ storeId: selectedStoreId, page, size: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHead
        title="Audit Logs"
        subtitle="Immutable trail of sensitive actions."
        actions={<StoreSelector allowAll />}
      />
      <Card>
        {q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.content.length === 0 ? (
          <Empty icon="📜" title="No audit entries" />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>When</th><th>Action</th><th>Entity</th>
                    <th>Entity ID</th><th>User</th><th>Store</th><th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.content.map((a) => (
                    <tr key={a.id}>
                      <td>{dateTime(a.createdAt)}</td>
                      <td><span className="badge badge-blue">{a.action}</span></td>
                      <td>{a.entityType || '—'}</td>
                      <td>{a.entityId || '—'}</td>
                      <td>{a.userId ?? '—'}</td>
                      <td>{a.storeId ?? '—'}</td>
                      <td className="mono" style={{ fontSize: 12, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.details ? (typeof a.details === 'string' ? a.details : JSON.stringify(a.details)) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row gap-sm" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                ← Prev
              </button>
              <span className="text-muted">Page {q.data.number + 1} of {Math.max(q.data.totalPages, 1)}</span>
              <button
                className="btn btn-secondary btn-sm"
                disabled={q.data.number + 1 >= q.data.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
